import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';

const SYSTEM_PROMPT = `You are GodfreyAI — a friendly, knowledgeable assistant representing Godfrey Kimani, a Lead Product Designer based in Nairobi, Kenya.

About Godfrey:
- Lead Product Designer at Safaricom PLC with 10+ years of experience
- Specialises in end-to-end product design: research, strategy, interaction design, and visual design
- Has shipped 20+ indie apps and products across fintech, enterprise SaaS, and consumer mobile
- Email: kimanigodfrey4@gmail.com
- LinkedIn: linkedin.com/in/kimanigodfrey

Key case studies:
1. My Safaricom Partner Portal Redesign (2023) — Unified 5 fragmented dealer portals into one SaaS platform. Reduced dealer task time by 40%, improved NPS significantly. Led end-to-end: research, IA, prototyping, design system, and handoff.
2. Dubi Survey & Feedback Platform — Built a survey intelligence platform surfacing patterns across 92M+ signals. Replaced 7 disconnected feedback tools with a single unified product. Features AI-powered weekly digests, journey NPS tracking, and segmentation.

Design philosophy:
- Believes great design is invisible — it removes friction rather than adding flair
- Advocates for research-grounded decisions and cross-functional collaboration
- Comfortable working in both 0→1 product creation and scaling mature design systems

Availability:
- Open to senior product design roles, consulting, and design leadership opportunities
- Available for mentorship conversations
- Interested in fintech, enterprise SaaS, and consumer mobile

Keep responses conversational, concise (2–4 sentences unless depth is needed), and warm. Always represent Godfrey authentically. If you don't know something specific, say so honestly and offer Godfrey's email for follow-up.`;

const QUICK_PROMPTS = [
  "I'm interested in mentorship",
  "I'd love to hire Godfrey",
  "Tell me about the Partner Portal case study",
  "Which case study should I start with?",
];

/* ─── Animations ─── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0)    scale(1);    }
`;

const blink = keyframes`
  0%, 80%, 100% { opacity: 0; }
  40%            { opacity: 1; }
`;

/* ─── Floating trigger ─── */
const Trigger = styled.button`
  position: fixed;
  bottom: 28px;
  right: 28px;
  z-index: 9000;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--color-primary);
  color: var(--color-bg);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 20px;
  letter-spacing: -0.02em;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  user-select: none;

  &:hover {
    transform: scale(1.07);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22);
  }
  &:active {
    transform: scale(0.97);
  }

  @media (max-width: 600px) {
    bottom: 88px;
    right: 16px;
  }
`;

/* ─── Modal overlay ─── */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 8999;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding: 0 28px 28px;

  @media (max-width: 600px) {
    padding: 0;
    align-items: flex-end;
    justify-content: stretch;
  }
`;

/* ─── Chat window ─── */
const Window = styled.div`
  width: 420px;
  max-width: calc(100vw - 56px);
  height: 620px;
  max-height: calc(100vh - 100px);
  background: #faf8f4;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.22);
  animation: ${fadeUp} 0.28s cubic-bezier(0.22, 1, 0.36, 1) both;

  @media (max-width: 600px) {
    width: 100%;
    max-width: 100%;
    height: 90vh;
    max-height: 90vh;
    border-radius: 20px 20px 0 0;
  }
`;

/* ─── Header ─── */
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
  flex-shrink: 0;
  background: #faf8f4;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const HeaderTitle = styled.span`
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 15px;
  color: #1a1a2e;
  letter-spacing: -0.02em;
`;

const HeaderSub = styled.span`
  font-family: var(--font-body);
  font-size: 12px;
  color: rgba(26, 26, 46, 0.5);
  font-weight: 400;
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.07);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(26, 26, 46, 0.6);
  transition: background 0.15s;
  &:hover { background: rgba(0, 0, 0, 0.12); }
`;

/* ─── Message area ─── */
const MessageArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
  display: flex;
  flex-direction: column;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.12); border-radius: 2px; }
`;

/* ─── Welcome screen ─── */
const Welcome = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px 4px 16px;
  flex: 1;
`;

const Avatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #e8e2d8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 22px;
  color: #1a1a2e;
  letter-spacing: -0.02em;
  margin-bottom: 20px;
  flex-shrink: 0;
  user-select: none;
`;

const WelcomeHeading = styled.h2`
  font-family: var(--font-body);
  font-size: 22px;
  font-weight: 700;
  color: #1a1a2e;
  letter-spacing: -0.03em;
  line-height: 1.25;
  margin-bottom: 20px;
`;

const QuickPrompts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const QuickBtn = styled.button`
  text-align: left;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.09);
  border-radius: 12px;
  padding: 14px 16px;
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 500;
  color: #1a1a2e;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  width: 100%;

  &:hover {
    background: #f0ebe2;
    border-color: rgba(0, 0, 0, 0.14);
  }
`;

/* ─── Messages ─── */
const MessageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 16px;
`;

const Bubble = styled.div`
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 14px;
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.55;
  white-space: pre-wrap;

  ${({ $role }) =>
    $role === 'user'
      ? `
    align-self: flex-end;
    background: #1a1a2e;
    color: #fff;
    border-bottom-right-radius: 4px;
  `
      : `
    align-self: flex-start;
    background: #fff;
    color: #1a1a2e;
    border: 1px solid rgba(0,0,0,0.08);
    border-bottom-left-radius: 4px;
  `}
`;

const TypingDots = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 4px 2px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(26, 26, 46, 0.35);
    animation: ${blink} 1.2s infinite;
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`;

/* ─── Input bar ─── */
const InputBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.07);
  background: #faf8f4;
  flex-shrink: 0;
`;

const Input = styled.input`
  flex: 1;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 24px;
  padding: 10px 16px;
  font-family: var(--font-body);
  font-size: 14px;
  color: #1a1a2e;
  outline: none;
  transition: border-color 0.15s;

  &::placeholder { color: rgba(26, 26, 46, 0.35); }
  &:focus { border-color: rgba(26, 26, 46, 0.3); }
`;

const SendBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ disabled }) => (disabled ? 'rgba(26,26,46,0.15)' : '#1a1a2e')};
  border: none;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, transform 0.1s;
  &:hover:not(:disabled) { background: #2d2d4e; transform: scale(1.05); }
`;

const SendIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

/* ─── Main component ─── */
const GodfreyAI = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 80);
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streaming]);

  const close = () => {
    abortRef.current?.abort();
    setOpen(false);
  };

  const send = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || streaming) return;

    const userMsg = { role: 'user', content: trimmed };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput('');
    setStreaming(true);

    const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    if (!apiKey || apiKey === 'your_openai_api_key_here') {
      setMessages([...next, { role: 'assistant', content: "API key not configured. Add your OpenAI key to the .env file as REACT_APP_OPENAI_API_KEY." }]);
      setStreaming(false);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          stream: true,
          messages: [
            { role: 'system', content: SYSTEM_PROMPT },
            ...next.map(({ role, content }) => ({ role, content })),
          ],
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error?.message || `HTTP ${res.status}`);
      }

      const placeholder = { role: 'assistant', content: '' };
      setMessages((prev) => [...prev, placeholder]);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          const trimLine = line.trim();
          if (!trimLine || trimLine === 'data: [DONE]') continue;
          if (!trimLine.startsWith('data: ')) continue;
          try {
            const json = JSON.parse(trimLine.slice(6));
            const delta = json.choices?.[0]?.delta?.content ?? '';
            if (delta) {
              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  ...updated[updated.length - 1],
                  content: updated[updated.length - 1].content + delta,
                };
                return updated;
              });
            }
          } catch {}
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Something went wrong: ${err.message}` },
      ]);
    } finally {
      setStreaming(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  const hasMessages = messages.length > 0;

  const modal = open
    ? ReactDOM.createPortal(
        <Overlay onClick={(e) => e.target === e.currentTarget && close()}>
          <Window>
            {/* Header */}
            <Header>
              <HeaderLeft>
                <HeaderTitle>GodfreyAI</HeaderTitle>
                <HeaderSub>Ask anything about Godfrey</HeaderSub>
              </HeaderLeft>
              <CloseBtn onClick={close} aria-label="Close">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </CloseBtn>
            </Header>

            {/* Messages / Welcome */}
            <MessageArea>
              {!hasMessages ? (
                <Welcome>
                  <Avatar>G</Avatar>
                  <WelcomeHeading>What would you like to know about Godfrey?</WelcomeHeading>
                  <QuickPrompts>
                    {QUICK_PROMPTS.map((p) => (
                      <QuickBtn key={p} onClick={() => send(p)}>
                        {p}
                      </QuickBtn>
                    ))}
                  </QuickPrompts>
                </Welcome>
              ) : (
                <MessageList>
                  {messages.map((m, i) => (
                    <Bubble key={i} $role={m.role}>
                      {m.content || (m.role === 'assistant' && streaming && i === messages.length - 1
                        ? <TypingDots><span /><span /><span /></TypingDots>
                        : null)}
                    </Bubble>
                  ))}
                  {streaming && messages[messages.length - 1]?.role === 'user' && (
                    <Bubble $role="assistant">
                      <TypingDots><span /><span /><span /></TypingDots>
                    </Bubble>
                  )}
                  <div ref={bottomRef} />
                </MessageList>
              )}
            </MessageArea>

            {/* Input */}
            <InputBar>
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Ask anything about Godfrey..."
                disabled={streaming}
              />
              <SendBtn
                onClick={() => send(input)}
                disabled={!input.trim() || streaming}
                aria-label="Send"
              >
                <SendIcon />
              </SendBtn>
            </InputBar>
          </Window>
        </Overlay>,
        document.body
      )
    : null;

  return (
    <>
      {ReactDOM.createPortal(
        <Trigger onClick={() => setOpen((v) => !v)} aria-label="Open GodfreyAI chat">
          G
        </Trigger>,
        document.body
      )}
      {modal}
    </>
  );
};

export default GodfreyAI;
